import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/shared";
import {
  LayoutDashboard, Users, Upload, Scale, ScrollText, LogOut, Menu, X, UserCircle, Landmark, CalendarDays, FileText, Vote,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Papan Pemuka / Dashboard", icon: LayoutDashboard, roles: ["admin", "secretariat"], end: true },
  { to: "/members", label: "Daftar Ahli / Members", icon: Users, roles: ["admin", "secretariat"] },
  { to: "/applications", label: "Permohonan / Applications", icon: FileText, roles: ["admin", "secretariat"] },
  { to: "/meetings", label: "Mesyuarat / Meetings", icon: CalendarDays, roles: ["admin", "secretariat"] },
  { to: "/voting", label: "Undian / Voting", icon: Vote, roles: ["admin", "secretariat"] },
  { to: "/import", label: "Import Excel", icon: Upload, roles: ["admin", "secretariat"] },
  { to: "/reconciliation", label: "Rekonsiliasi / Reconcile", icon: Scale, roles: ["admin", "secretariat"] },
  { to: "/audit", label: "Log Audit / Audit Log", icon: ScrollText, roles: ["admin"] },
  { to: "/portal", label: "Profil Saya / My Profile", icon: UserCircle, roles: ["member"] },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Landmark className="text-brass" strokeWidth={1.5} size={26} />
          <div>
            <div className="font-serif text-xl text-paper leading-none tracking-tight">KKPKLB</div>
            <div className="text-[10px] text-paper/50 mt-1 uppercase tracking-[0.15em]">Membership Register</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-4 ${
                  isActive
                    ? "border-brass bg-white/5 text-paper font-medium"
                    : "border-transparent text-paper/60 hover:text-paper hover:bg-white/5"
                }`
              }
            >
              <Icon size={18} strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-brass/20 flex items-center justify-center text-brass font-serif">
            {user?.name?.[0] || "?"}
          </div>
          <div className="min-w-0">
            <div className="text-paper text-sm truncate">{user?.name}</div>
            <div className="mt-0.5"><RoleBadge role={user?.role} /></div>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-paper/70 hover:text-paper hover:bg-white/5 rounded-sm transition-colors"
        >
          <LogOut size={16} /> Log Keluar / Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-paper">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-ink z-40">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 bg-ink/40 z-40" onClick={() => setOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-64 bg-ink z-50 animate-fade-in">{SidebarInner}</aside>
        </>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-sm border-b border-hairline">
          <div className="flex items-center justify-between px-4 md:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden text-ink" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
                <Menu size={22} />
              </button>
              <div className="min-w-0">
                <h1 className="font-serif text-xl md:text-2xl text-ink tracking-tight truncate">{title}</h1>
                {subtitle && <p className="text-xs md:text-sm text-slate truncate">{subtitle}</p>}
              </div>
            </div>
            {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
          </div>
        </header>
        <main className="p-4 md:p-8 lg:p-10 animate-fade-in">{children}</main>
      </div>
    </div>
  );
}
